import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { assetCatalog, drivers, registry, type AssetMemberResult, type RegAsset, type RegAssetSlot, type ShipResult } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Field } from "@/components/Field";
import { KV } from "@/components/KV";
import { StatGrid } from "@/components/StatGrid";
import { Select, SearchInput } from "@/components/inputs";
import { ago, dateTime, POLL } from "@/lib/format";
import { CAP, useSession } from "@/auth/session";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { kindLabel, useTemplates } from "@/modules/asset_catalog/common";
import { CompanySelect, ModelPicker, PartChip, RQ, useCompanyScope } from "@/modules/registry/parts_common";

import { ActionRow } from "@/components/ActionRow";
const ELLIPSIS: React.CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

/** Assets: vehicles and batteries as registered units — enrol against a
 *  catalog model, see the BOM slots filled vs expected, install parts,
 *  allocate (parts follow). */
export function AssetsInventory() {
  const nav = useNavigate();
  const tpl = useTemplates().data;
  const { companies, scoped } = useCompanyScope();
  const [type, setType] = useState("");
  const [company, setCompany] = useState("");
  const [search, setSearch] = useState("");

  const stats = useQuery({ queryKey: [...RQ, "parts", "stats"], queryFn: registry.partsStats, refetchInterval: POLL.LIST });
  const q = useQuery({
    queryKey: [...RQ, "assets", type, company, search],
    queryFn: () => registry.assets({ asset_type: type || undefined, company: company || undefined, search: search || undefined, limit: "500" }),
    refetchInterval: POLL.LIST,
  });

  const cols = useMemo<DataCol<RegAsset>[]>(() => [
    { key: "id", label: "Asset ID", width: 170, mono: true, render: (a) => <span style={{ fontWeight: 600 }}>{a.asset_id}</span> },
    { key: "type", label: "Type", width: 90, render: (a) => kindLabel(tpl, a.asset_type) },
    { key: "model", label: "Model", width: 200, render: (a) => a.model ?? <span style={{ color: "var(--bad)" }}>no model</span> },
    { key: "label", label: "Label", width: 140, render: (a) => a.label ?? "—" },
    { key: "owner", label: "Owner", width: 140, render: (a) => a.tenant ?? a.tenant_id },
    { key: "custodian", label: "Held by", width: 140,
      render: (a) => a.custodian_id === a.tenant_id
        ? <span className="muted">owner</span>
        : <span style={{ color: "var(--warn)" }}>{a.custodian ?? a.custodian_id}</span> },
    { key: "state", label: "State", width: 100, render: (a) => <PartChip st={a.state} /> },
    { key: "parts", label: "Parts", width: 70, mono: true, render: (a) => a.parts_installed },
    { key: "created", label: "Enrolled", width: 90, mono: true, render: (a) => ago(a.created_at), title: (a) => dateTime(a.created_at) },
    { key: "open", label: "", width: 90, render: (a) => (
      <button className="btn ghost" onClick={(e) => { e.stopPropagation();
        nav(`/registry/pair-repair?id=${encodeURIComponent(a.asset_id)}`); }}
        title="Parts, allocation and driver for this asset">Open →</button>) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [tpl]);

  const st = stats.data;

  return (
    <div>
      <StatGrid stats={[
        { k: "Assets", v: st?.assets ?? "—" },
        { k: "In inventory", v: st?.assets_inventory ?? "—", s: "not deployed" },
        { k: "Parts fitted", v: st?.installed ?? "—" },
        { k: "Parts in inventory", v: st?.inventory ?? "—" },
      ]} />
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search asset id / label…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option><option value="vehicle">Vehicle</option><option value="battery">Battery</option>
        </Select>
        <CompanySelect value={company} onChange={setCompany} companies={companies} scoped={scoped} />
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>{q.data ? `${q.data.total} assets` : ""}</span>
      </div>
      <DataTable rows={q.data?.assets ?? []} cols={cols} rowKey={(r) => r.asset_id} pageSize={100}
        onRowClick={(r) => nav(`/registry/pair-repair?id=${encodeURIComponent(r.asset_id)}`)}
        empty={<div className="panel panel-pad cap">
          No assets yet — enrol one on the Enroll page (What = Asset).
        </div>} />

    </div>
  );
}

export function EnrollAssetForm({ owner, ownerField, onDone }: {
  /** See EnrollPartForm. Chosen once for the page, used by every enrol door. */
  owner: string;
  /** The picker itself, placed by the form as its first titled group. */
  ownerField: React.ReactNode;
  onDone: (m: string) => void;
}) {
  const qc = useQueryClient();
  // ENROL — hiding is UX; require_cap on the route is the enforcement.
  const { can: allowed } = useSession();
  if (!allowed(CAP.ENROL)) {
    return <div className="cap">Enrolling creates platform identities — a super-admin operation.</div>;
  }

  const fb = useMutationFeedback();
  const tpl = useTemplates().data;
  const [kind, setKind] = useState("");
  const [modelId, setModelId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [label, setLabel] = useState("");
  // production build: per-slot pick, narrowed step by step (manufacturer →
  // model → serial) — a flat serial dropdown would be massive at real stock
  // volumes. enroll + install happen in ONE all-or-nothing call server-side.
  const [picks, setPicks] = useState<Record<string, { mfr: string; model: string; serial: string }>>({});
  const slots = kind ? (tpl?.slots[kind] ?? []) : [];
  const stock = useQuery({ queryKey: [...RQ, "parts", "stock", "build"], enabled: slots.length > 0,
    queryFn: () => registry.parts({ state: "inventory", limit: "500" }) });
  // an asset built FOR a company may use that company's own parts as well as
  // Parts the chosen owner actually holds. `tenant_id === null` used to mean
  // "Aphelion stock" and can never be true now — owners are NOT NULL and stock
  // is a STATE (REGISTRY.md §10). An empty picker here means that company has
  // nothing in inventory, which is the truth rather than a bug.
  // Only the CHOSEN OWNER's stock may be built into their asset. Previously
  // this keyed off the form's own company box, which defaulted to "" and so
  // offered nothing until you picked one; now it follows the page's owner, so
  // the parts on offer are exactly the ones the new asset may lawfully contain.
  const inStock = (stock.data?.parts ?? []).filter((p) => !!owner && p.tenant_id === owner);
  // the ASSET model's catalog BOM is the allow-list: a slot that names child
  // models (two motor vendors, say) only offers stock of exactly those models;
  // a slot the catalog leaves open falls back to kind-allowed stock
  const detail = useQuery({ queryKey: ["asset-catalog", "model", modelId], enabled: !!modelId,
    queryFn: () => assetCatalog.model(modelId) });
  const bomFor = (slot: string) => (detail.data?.bom ?? []).filter((b) => b.slot === slot);
  const pickedSerials = Object.values(picks).map((v) => v.serial).filter(Boolean);
  const buildParts = slots.flatMap((sl) => picks[sl.slot]?.serial
    ? [{ slot: sl.slot, serial_no: picks[sl.slot].serial }] : []);
  const enroll = useMutation({
    mutationFn: async () => {
      const base = { asset_id: assetId.trim(), version_id: modelId, label: label.trim() || undefined,
        company_id: owner };
      if (buildParts.length) return registry.buildAsset({ ...base, parts: buildParts });
      const a = await registry.enrollAsset(base);
      return { asset_id: a.asset_id, installed: [], warnings: [] };
    },
    onSuccess: (r) => {
      fb.ok(); void qc.invalidateQueries({ queryKey: [...RQ] });
      onDone(`${r.asset_id} enrolled${r.installed.length ? ` · ${r.installed.length} subcomponent${r.installed.length === 1 ? "" : "s"} installed` : ""}${r.warnings.length ? ` · ⚠ ${r.warnings.join("; ")}` : ""}`);
      setAssetId(""); setLabel(""); setPicks({});
    },
    onError: fb.fail,
  });
  return (
    <div>
      {/* Owner first, as its own group — who will own the asset is a fact about
          the asset, answered before its VIN is typed. Matches the part form. */}
      <div className="section-label">Owner</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
        <Field label="Company" req>{ownerField}</Field>
      </div>

      <div className="section-label tight">Identity</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
        <ModelPicker kinds={["vehicle", "battery"]} kind={kind} onKind={(k) => { setKind(k); setPicks({}); }} modelId={modelId} onModel={setModelId} />
        <Field label={kind === "vehicle" ? "VIN" : kind === "battery" ? "BIN (pack serial)" : "Asset ID"} req>
          <SearchInput className="fill" mono value={assetId} onChange={(e) => setAssetId(e.target.value)} />
        </Field>
        {kind === "vehicle" && (
          <Field label="Number plate" req>
            <SearchInput className="fill" mono value={label} onChange={(e) => setLabel(e.target.value)} placeholder="MH-12-KL-4471" />
          </Field>
        )}
      </div>

      {slots.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="section-label">Build — install subcomponents from stock (optional)</div>
          <div style={{ display: "grid", gap: 8 }}>
            {slots.map((sl) => {
              const v = picks[sl.slot] ?? { mfr: "", model: "", serial: "" };
              const expected = bomFor(sl.slot);
              const expectedIds = new Set(expected.map((b) => b.version_id));
              const slotStock = inStock.filter((p) => sl.allow.includes(p.kind)
                && (expectedIds.size === 0 || expectedIds.has(p.version_id))
                && (!pickedSerials.includes(p.serial_no) || v.serial === p.serial_no));
              const mfrs = [...new Set(slotStock.map((p) => p.manufacturer))].sort();
              const models = [...new Map(slotStock.filter((p) => p.manufacturer === v.mfr)
                .map((p) => [p.version_id, `${p.model} v${p.model_version}`])).entries()];
              const serials = slotStock.filter((p) => p.version_id === v.model);
              const setSlot = (nv: { mfr: string; model: string; serial: string }) =>
                setPicks((prev) => ({ ...prev, [sl.slot]: nv }));
              return (
                <div key={sl.slot} style={{ display: "grid", alignItems: "center", gap: 8,
                  gridTemplateColumns: "minmax(90px,140px) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)" }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }} title={expected.length
                    ? `catalog allows: ${expected.map((b) => `${b.name} v${b.version} (${b.manufacturer})`).join(", ")}`
                    : `catalog leaves this slot open — any ${sl.allow.join("/")} model`}>
                    {sl.role}{sl.min > 0 && <span style={{ color: "var(--orange)" }}> *</span>}
                    <span style={{ color: "var(--t-dim)", fontWeight: 400 }}> ({slotStock.length})</span>
                  </span>
                  <Select className="fill" value={v.mfr}
                    onChange={(e) => setSlot({ mfr: e.target.value, model: "", serial: "" })}>
                    <option value="">{mfrs.length ? "— manufacturer —"
                      : expected.length ? "— no stock of the catalog's models —" : "— none in stock —"}</option>
                    {mfrs.map((m) => <option key={m} value={m}>{m}</option>)}
                  </Select>
                  <Select className="fill" value={v.model} disabled={!v.mfr}
                    onChange={(e) => setSlot({ ...v, model: e.target.value, serial: "" })}>
                    <option value="">{v.mfr ? "— model —" : ""}</option>
                    {models.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </Select>
                  <Select className="fill" value={v.serial} disabled={!v.model}
                    onChange={(e) => setSlot({ ...v, serial: e.target.value })}>
                    <option value="">{v.model ? `— serial (${serials.length} in stock) —` : ""}</option>
                    {serials.map((p) => <option key={p.part_id} value={p.serial_no}>{p.serial_no}</option>)}
                  </Select>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 8 }}>
            Narrow each slot: manufacturer → model → the unit's serial. The options are the SUBSET the vehicle
            model's catalog BOM allows (hover a slot name to see it) — a slot the catalog leaves open offers any
            model of the allowed kind. One transaction — a bad pick rejects the whole build. Slots marked * are
            required by the catalog; you can still enroll without them and install later from the asset drawer.
            Transfer moves the asset with everything installed; tick “ship it” to move custody too.
          </div>
        </div>
      )}

      <ActionRow>
        <button className="btn teal" disabled={!owner || !modelId || !assetId.trim() || (kind === "vehicle" && !label.trim()) || enroll.isPending}
          onClick={() => enroll.mutate()}>
          {buildParts.length ? `Enroll & install ${buildParts.length} subcomponent${buildParts.length === 1 ? "" : "s"}` : "Enroll asset"}
        </button>
        {fb.errorNode}
      </ActionRow>
    </div>
  );
}

export function SlotRow({ asset, slot, onChanged }: { asset: RegAsset; slot: RegAssetSlot; onChanged: () => void }) {
  const fb = useMutationFeedback();
  const tpl = useTemplates().data;
  const [pick, setPick] = useState("");
  const [reason, setReason] = useState("restock");
  const stock = useQuery({ queryKey: [...RQ, "parts", "stock"], queryFn: () => registry.parts({ state: "inventory", limit: "500" }) });
  // R-O4: a part can only be fitted to an asset with the SAME owner, and it
  // must be in the same place (same custodian) — you cannot bolt a cluster
  // sitting in Pune into a vehicle in Delhi. The server enforces both; the
  // picker just does not offer what would be refused.
  const candidates = (stock.data?.parts ?? []).filter(
    (p) => slot.allow.includes(p.kind)
      && p.tenant_id === asset.tenant_id
      && p.custodian_id === asset.custodian_id);
  // INSTALL_REMOVE is a Pro feature; the BOM itself stays readable.
  const { can: canDo } = useSession();
  const install = useMutation({
    mutationFn: (partId: string) => registry.installPart(partId, { asset_id: asset.asset_id, slot: slot.slot }),
    onSuccess: (r) => { fb.ok(); setPick(""); onChanged(); if (r.warning) fb.fail(new Error(r.warning)); },
    onError: fb.fail,
  });
  const remove = useMutation({
    mutationFn: (partId: string) => registry.removePart(partId, reason),
    onSuccess: () => { fb.ok(); onChanged(); }, onError: fb.fail,
  });
  const color = slot.installed.length === 0 ? (slot.min > 0 ? "var(--bad)" : "var(--t-dim)") : slot.ok ? "var(--ok)" : "var(--warn)";
  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid var(--line)" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5 }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: color, flex: "0 0 auto" }} />
        <span style={{ fontWeight: 600, minWidth: 120 }}
          title={`expects ${slot.expected.length ? slot.expected.join(" / ") : `any ${slot.allow.map((a) => kindLabel(tpl, a)).join(" / ")}`} · ${slot.installed.length}/${slot.max} fitted`}>
          {slot.role}{slot.min > 0 && <span style={{ color: "var(--orange)" }}> *</span>}
        </span>
      </div>
      {slot.installed.map((p) => (
        // grid, not flex: free-flowing spans wrapped serials mid-word
        <div key={p.part_id} style={{ display: "grid", alignItems: "center", gap: 10, fontSize: 12.5,
          marginLeft: 18, marginTop: 6,
          gridTemplateColumns: "minmax(0,1.1fr) minmax(0,1.4fr) minmax(0,1.2fr) minmax(0,1fr) auto auto" }}>
          <span className="mono" style={ELLIPSIS} title={p.serial_no}>{p.serial_no}</span>
          <span style={ELLIPSIS} title={p.model}>{p.model}</span>
          <span style={{ ...ELLIPSIS, color: "var(--t-mute)" }} title={p.manufacturer}>{p.manufacturer}</span>
          <span className="mono" style={{ ...ELLIPSIS, color: "var(--t-mute)" }}>{p.connection_id ?? ""}</span>
          <span className="cap">fitted</span>
          {canDo(CAP.INSTALL_REMOVE) && (
            <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <Select value={reason} onChange={(e) => setReason(e.target.value)} style={{ minWidth: 0, width: 170 }}>
                <option value="restock">back to inventory</option><option value="repair">to repair</option><option value="scrapped">scrapped</option>
              </Select>
              <button className="btn red" disabled={remove.isPending} onClick={() => remove.mutate(p.part_id)}>Uninstall</button>
            </span>
          )}
        </div>
      ))}
      {slot.installed.length < slot.max && canDo(CAP.INSTALL_REMOVE) && (
        <div style={{ display: "flex", gap: 6, marginLeft: 18, marginTop: 6 }}>
          <Select value={pick} onChange={(e) => setPick(e.target.value)} style={{ flex: 1, minWidth: 0 }}>
            <option value="">{candidates.length ? "— install a part from stock —" : "— no matching part in stock —"}</option>
            {candidates.map((p) => (
              <option key={p.part_id} value={p.part_id}>
                {p.serial_no} · {p.model} v{p.model_version} · {p.manufacturer}{p.connection_id ? ` · ${p.connection_id}` : ""}
                {slot.expected_version_ids.length && !slot.expected_version_ids.includes(p.version_id) ? " (not the catalog model)" : ""}
              </option>
            ))}
          </Select>
          <button className="btn teal" disabled={!pick || install.isPending} onClick={() => install.mutate(pick)}>Install</button>
        </div>
      )}
      {fb.errorNode && <div style={{ marginLeft: 18, marginTop: 4 }}>{fb.errorNode}</div>}
    </div>
  );
}

/** Move an asset through its life: inventory · deployed · repair · scrapped.
 *
 *  Sits beside Transfer on purpose — REGISTRY.md §10 has THREE write doors and
 *  they change different things. Transfer moves the OWNER, Ship moves the
 *  CUSTODIAN, this moves the STATE. Presenting them together is what stops
 *  "send it for repair" being done by transferring it to the repairer, which is
 *  the mistake the old single-column model actively encouraged.
 *
 *  Scrap is confirmed and styled as destructive because it is terminal on the
 *  server: un-scrapping is refused, since it would silently revive something
 *  already written off. The rules are enforced server-side (R-O1/R-O3/R-O6);
 *  this only has to explain them, and it surfaces the refusal verbatim when it
 *  comes — a message like "you cannot deploy something you do not have" reads
 *  better than anything generic offered here.
 */
export function AssetState({ asset, onChanged, bare }: { asset: RegAsset; onChanged: () => void; bare?: boolean }) {
  const { can } = useSession();
  const fb = useMutationFeedback();
  const [state, setState] = useState("");
  const [msg, setMsg] = useState("");
  const away = asset.custodian_id && asset.custodian_id !== asset.tenant_id;

  const go = useMutation({
    mutationFn: () => registry.setAssetState(asset.asset_id, { state }),
    onSuccess: (r) => {
      fb.ok();
      const moved = r.parts_moved
        ? ` · ${r.parts_moved} installed part${r.parts_moved === 1 ? "" : "s"} moved with it`
        : "";
      setMsg(r.action === "noop" ? `already ${r.state} — nothing changed`
                                 : `${r.from} → ${r.state}${moved}`);
      onChanged();
    },
    onError: fb.fail,
  });

  // Hiding is UX; require_cap(CHANGE_STATE) on the route is the enforcement.
  // Shown as a plain statement rather than omitted silently, so somebody who
  // expects the control learns why it is not there.
  if (!can(CAP.CHANGE_STATE)) {
    return <div className="cap">Currently {asset.state}. Your plan and role do not include changing lifecycle state.</div>;
  }

  if (asset.state === "scrapped") {
    return (
      <div className="cap">
        Scrapped — this is final. Correcting a mistaken scrap is a data fix, not
        a state change.
      </div>
    );
  }

  return (
    <div style={bare ? undefined : { marginTop: 18 }}>
      {!bare && <div className="section-label">Lifecycle state</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Move to">
          <Select value={state} onChange={(e) => setState(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">Choose…</option>
            <option value="inventory">Inventory — in stock</option>
            <option value="deployed" disabled={!!away}>
              {away ? "Deployed — needs to be here first" : "Deployed — in service"}
            </option>
            <option value="repair">Repair — being worked on</option>
            <option value="scrapped">Scrapped — final</option>
          </Select>
        </Field>
        <button
          className={`btn ${state === "scrapped" ? "red" : "teal"}`}
          disabled={!state || go.isPending}
          onClick={() => {
            if (state === "scrapped" &&
                !window.confirm(`Scrap ${asset.asset_id}? This cannot be undone, and every installed part is scrapped with it.`)) return;
            go.mutate();
          }}>
          {state === "scrapped" ? "Scrap" : "Change state"}
        </button>
        {fb.errorNode}{!fb.err && msg && <span style={{ color: "var(--ok)", fontSize: 12 }}>{msg}</span>}
      </div>
      {away && (
        // R-O1, explained where it bites rather than as a rejection later.
        <div className="cap" style={{ marginTop: 6 }}>
          Held by {asset.custodian ?? asset.custodian_id}, so it cannot be
          deployed — you cannot deploy something you do not have. Ship it back
          first.
        </div>
      )}
    </div>
  );
}

/** CUSTODY — who has it, and what is on its way (REGISTRY.md §10, D-82).
 *
 *  Sits beside Transfer (ownership) and Lifecycle (state) because they are the
 *  THREE write doors and they change different things. This one is the door
 *  that had no console at all: `/ship` existed server-side and nothing in the
 *  UI could call it, so "send it back from repair" was unreachable.
 *
 *  Shipping RECORDS INTENT and hands nothing over. Custody stays with the
 *  sender — they have the goods and they are who you chase — until the
 *  receiver accepts. So this renders one of three things:
 *
 *    settled      "Held by X" + Ship somewhere
 *    outbound     "In transit to Y since <date>" + Cancel (you still hold it)
 *    inbound      "On its way to you from X" + Accept
 *
 *  "In transit" is derived from `custodian_pending`, never a state value, so
 *  the life stage (deployed / repair / inventory) survives the journey.
 */
export function CustodySection({ asset, companies, onChanged, bare }: {
  asset: RegAsset; companies: { company_id: string; name: string }[];
  onChanged: () => void; bare?: boolean;
}) {
  const { me, can: allowed } = useSession();
  const fb = useMutationFeedback();
  // Hoisted: the picker below sits inside `{!inTransit && ...}`, and a hook
  // called from a conditional branch breaks hook ordering the moment the
  // asset goes into transit.
  const { scoped } = useCompanyScope();
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("");

  const inTransit = !!asset.custodian_pending;
  const iAmReceiver = asset.custodian_pending === me?.company_id;
  const name = (id: string | null) =>
    (id && companies.find((c) => c.company_id === id)?.name) || id || "—";

  const act = (fn: () => Promise<{ results: ShipResult[] }>, said: (r: ShipResult) => string) =>
    useMutation({
      mutationFn: fn,
      onSuccess: (r) => {
        fb.ok();
        const x = r.results[0];
        // Plain words: "in_transit" / "noop" are our vocabulary, not the
        // operator's, and the reason the server gives is better than anything
        // generic we could substitute for it.
        setMsg(x.reason ?? said(x));
        onChanged();
      },
      onError: fb.fail,
    });

  const send = act(() => registry.ship({ asset_ids: [asset.asset_id], to_company: to }),
                   (x) => `on its way to ${name(x.to ?? to)} — they accept it on arrival`);
  const accept = act(() => registry.receive({ asset_ids: [asset.asset_id] }),
                     (x) => `received${x.parts_moved ? ` · ${x.parts_moved} fitted part${x.parts_moved === 1 ? "" : "s"} arrived with it` : ""}`);
  const cancel = act(() => registry.cancelShipment({ asset_ids: [asset.asset_id] }),
                     () => "shipment called off — it never left your custody");

  if (!allowed(CAP.SHIP)) {
    return (
      <div className="cap">
        Held by <strong>{name(asset.custodian_id)}</strong>
        {inTransit && <> · on its way to <strong>{name(asset.custodian_pending)}</strong></>}
      </div>
    );
  }

  return (
    <div style={bare ? undefined : { marginTop: 18 }}>
      {!bare && <div className="section-label">Custody</div>}

      {!inTransit && (
        <ActionRow>
          <Field label={`Held by ${name(asset.custodian_id)} · ship to`}>
            <CompanySelect value={to} onChange={setTo} companies={companies}
                           scoped={scoped}
                           allLabel={null} style={{ minWidth: 220 }} />
          </Field>
          <button className="btn teal" disabled={!to || send.isPending}
                  onClick={() => send.mutate()}>Ship</button>
        </ActionRow>
      )}

      {inTransit && (
        <div>
          <div className="cap" style={{ marginBottom: 8 }}>
            {iAmReceiver
              ? <>On its way to you from <strong>{name(asset.custodian_id)}</strong>
                  {asset.shipped_at && <> · sent {ago(asset.shipped_at)}</>}. It is
                  not yours to use until you accept it.</>
              : <>In transit to <strong>{name(asset.custodian_pending)}</strong>
                  {asset.shipped_at && <> · sent {ago(asset.shipped_at)}</>}. You still
                  hold it until they accept.</>}
          </div>
          <ActionRow>
            {iAmReceiver
              ? <button className="btn teal" disabled={accept.isPending}
                        onClick={() => accept.mutate()}>Accept delivery</button>
              : <button className="btn ghost" disabled={cancel.isPending}
                        onClick={() => cancel.mutate()}>Cancel shipment</button>}
          </ActionRow>
        </div>
      )}

      <div style={{ marginTop: 6 }}>
        {fb.errorNode}{!fb.err && msg && <span style={{ color: "var(--ok)", fontSize: 12 }}>{msg}</span>}
      </div>
    </div>
  );
}

export function TransferAsset({ asset, companies, onChanged, bare }: { asset: RegAsset; companies: { company_id: string; name: string }[]; onChanged: () => void; bare?: boolean }) {
  const { can } = useSession();
  const fb = useMutationFeedback();
  const { scoped } = useCompanyScope();
  const [company, setCompany] = useState("");
  const [ship, setShip] = useState(true);
  const [msg, setMsg] = useState("");
  const alloc = useMutation({
    mutationFn: () => registry.transferAssets({ asset_ids: [asset.asset_id], company_id: company, ship }),
    onSuccess: (r) => {
      fb.ok();
      const x = r.results[0];
      // plain words — "noop"/"reassign" are our vocabulary, not the operator's
      const name = companies.find((c) => c.company_id === company)?.name ?? "that company";
      // Plain words — "noop"/"transferred" are our vocabulary, not the
      // operator's. The custody half is stated explicitly because "sold but
      // still here" is a real outcome someone has to be able to see.
      const said: Record<string, string> = {
        noop: `already owned by ${name} — nothing changed`,
        transferred: ship
          ? `transferred to ${name}, and shipped`
          : `transferred to ${name} — still held by the previous custodian`,
        invalid: x.reason ?? "not registered",
      };
      const moved = x.parts_moved ? ` · ${x.parts_moved} installed part${x.parts_moved === 1 ? "" : "s"} followed` : "";
      setMsg((said[x.action] ?? x.action) + moved);
      onChanged();
    },
    onError: fb.fail,
  });
  // Transfer is a SALE, and TRANSFER_OWNERSHIP is internal-only in the matrix:
  // a customer never sells an asset through this console. Showing the control
  // to them would be a button that 403s.
  if (!can(CAP.TRANSFER_OWNERSHIP)) {
    return (
      <div className="cap">
        Owned by {asset.tenant ?? asset.tenant_id}
        {asset.custodian_id && asset.custodian_id !== asset.tenant_id
          ? ` · held by ${asset.custodian ?? asset.custodian_id}` : ""}.
        Ownership changes on a sale, which is a platform operation.
      </div>
    );
  }

  return (
    <div style={bare ? undefined : { marginTop: 18 }}>
      {!bare && <div className="section-label">Transfer ownership</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="New owner (installed parts follow)">
          <CompanySelect value={company} onChange={setCompany} companies={companies} scoped={scoped} allLabel={null} style={{ minWidth: 220 }} />
        </Field>
        {/* Ownership and custody are separate (REGISTRY.md §10). Ticked by
            default because the ordinary sale is sold-and-shipped; untick it for
            sold-to-B-delivered-to-C, or goods still on the seller's floor. */}
        <label style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 8 }}
               title="Also move CUSTODY to the new owner. Untick if the goods stay where they are, or ship somewhere else.">
          <input type="checkbox" checked={ship} onChange={(e) => setShip(e.target.checked)} />
          <span className="cap">Ship it too</span>
        </label>
        <button className="btn teal" disabled={!company || alloc.isPending} onClick={() => alloc.mutate()}>Transfer</button>
        {fb.errorNode}{!fb.err && msg && <span style={{ color: "var(--ok)", fontSize: 12 }}>{msg}</span>}
      </div>
    </div>
  );
}


/** Driver <-> asset: current holder, allocate to an ACTIVE driver, release. */
/** Who MAY drive this asset (D-75) — distinct from who currently HOLDS the key.
 *
 * A vehicle is shared by a shift roster: several members, exactly one holder,
 * and adding a member must never disturb whoever is driving. The holder is set
 * by DriverSection below; this only changes the roster.
 */
export function MembersSection({ asset, onChanged, bare }: { asset: RegAsset; onChanged: () => void; bare?: boolean }) {
  // ASSIGN_DRIVER — handing over the key is a fleet operation, granted
  // to company_admin AND company_user on every tier.
  const { can: canDo } = useSession();
  if (!canDo(CAP.ASSIGN_DRIVER)) {
    return <div className="cap">Driver assignment is not included for your role.</div>;
  }
  const fb = useMutationFeedback();
  const [pick, setPick] = useState("");
  const [msg, setMsg] = useState("");
  const list = useQuery({ queryKey: [...RQ, "asset-members", asset.asset_id],
    queryFn: () => registry.assetMembers(asset.asset_id) });
  const active = useQuery({ queryKey: [...RQ, "drivers-active"],
    queryFn: () => drivers.list({ status: "active", limit: "500" }) });

  const done = (r: AssetMemberResult) => {
    fb.ok();
    const who = r.driver_name ?? r.driver;
    setMsg(r.action === "noop" ? `${who} is already a driver here`
      : r.action === "added" ? `${who} added`
      // removing the holder also takes the key back and retires their grant —
      // say so, or the operator will not realise the vehicle just changed hands
      : `${who} removed${r.key_taken_back ? " — they held the key, so it was taken back" : ""}`);
    setPick(""); void list.refetch(); onChanged();
  };
  const add = useMutation({ mutationFn: (d: string) => registry.addAssetMember(asset.asset_id, d),
    onSuccess: done, onError: fb.fail });
  const drop = useMutation({ mutationFn: (d: string) => registry.removeAssetMember(asset.asset_id, d),
    onSuccess: done, onError: fb.fail });

  const members = list.data?.members ?? [];
  const memberIds = new Set(members.map((m) => m.driver_id));
  const addable = (active.data?.drivers ?? []).filter((d) => !memberIds.has(d.driver_id));

  return (
    <div style={bare ? undefined : { marginTop: 18 }}>
      {!bare && <div className="section-label">Drivers ({members.length})</div>}
      {members.length === 0 ? (
        <div className="cap">
          No drivers.
        </div>
      ) : members.map((m) => (
        <div key={m.driver_id} style={{ display: "flex", gap: 10, alignItems: "center",
          fontSize: 12.5, flexWrap: "wrap", padding: "4px 0" }}>
          <span style={{ fontWeight: m.is_holder ? 600 : 400 }}>{m.name}</span>
          <span className="mono muted">{m.code} · {m.msisdn}</span>
          {m.is_holder && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ok)",
              border: "1px solid var(--ok)", borderRadius: 3, padding: "1px 6px" }}>
              has the key
            </span>
          )}
          <span className="muted">since {dateTime(m.since)}</span>
          <button className="btn red" style={{ marginLeft: "auto" }}
            disabled={drop.isPending}
            title={m.is_holder
              ? "This driver currently holds the key. Removing them takes it back and retires their grant."
              : "Remove from this vehicle's drivers"}
            onClick={() => drop.mutate(m.driver_id)}>Remove</button>
        </div>
      ))}
      {asset.tenant_id && (
        <ActionRow top={8}>
          <Select value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 240 }}>
            <option value="">— add another driver —</option>
            {addable.map((dr) => (
              <option key={dr.driver_id} value={dr.driver_id}>{dr.name} · {dr.code} · {dr.msisdn}</option>
            ))}
          </Select>
          <button className="btn teal" disabled={!pick || add.isPending} onClick={() => add.mutate(pick)}>
            Add driver
          </button>
          <span className="cap">
            Adding a driver does not hand over the key.
          </span>
          {fb.errorNode}{!fb.err && msg && <span style={{ color: "var(--ok)", fontSize: 12 }}>{msg}</span>}
        </ActionRow>
      )}
    </div>
  );
}

export function DriverSection({ asset, onChanged, bare }: { asset: RegAsset; onChanged: () => void; bare?: boolean }) {
  // ASSIGN_DRIVER — handing over the key is a fleet operation, granted
  // to company_admin AND company_user on every tier.
  const { can: canDo } = useSession();
  if (!canDo(CAP.ASSIGN_DRIVER)) {
    return <div className="cap">Driver assignment is not included for your role.</div>;
  }
  const fb = useMutationFeedback();
  const [pick, setPick] = useState("");
  const [lease, setLease] = useState("");     // days the APP may run offline; "" = server default
  const [msg, setMsg] = useState("");
  const info = useQuery({ queryKey: [...RQ, "asset-driver", asset.asset_id],
    queryFn: () => registry.assetDriver(asset.asset_id) });
  const active = useQuery({ queryKey: [...RQ, "drivers-active"],
    queryFn: () => drivers.list({ status: "active", limit: "500" }) });
  const set = useMutation({
    mutationFn: (driver: string) => registry.setAssetDriver(asset.asset_id, driver,
      lease.trim() ? Number(lease) : null),
    onSuccess: (r) => {
      fb.ok();
      const who = r.driver_name ?? "that driver";
      const said: Record<string, string> = {
        allocated: `paired with ${who}`, reassign: `reassigned to ${who}`,
        reassigned: `reassigned to ${who}`, noop: `already with ${who} — nothing changed`,
        lease_updated: `app lease updated for ${who}`,
      };
      setMsg(said[r.action] ?? `${r.action}${r.driver_name ? ` → ${r.driver_name}` : ""}`);
      setPick(""); void info.refetch(); onChanged();
    },
    onError: fb.fail,
  });
  const release = useMutation({
    mutationFn: () => registry.releaseAssetDriver(asset.asset_id),
    onSuccess: () => { fb.ok(); setMsg("driver released"); void info.refetch(); onChanged(); },
    onError: fb.fail,
  });
  const cur = info.data?.current;
  return (
    <div style={bare ? undefined : { marginTop: 18 }}>
      {!bare && <div className="section-label">Driver</div>}
      {cur ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600 }}>{cur.name}</span>
          <span className="mono muted">{cur.code} · {cur.msisdn}</span>
          <span className="muted">since {dateTime(cur.valid_from)}</span>
          <span className="muted">
            app lease {cur.lease_days ? `${cur.lease_days} d` : "default"}
          </span>
          <button className="btn ghost" style={{ marginLeft: "auto" }} disabled={release.isPending}
            onClick={() => release.mutate()}>Release</button>
        </div>
      ) : (
        <div className="cap">
          No driver{asset.tenant_id ? "" : " — allocate the asset to a company first"}.
        </div>
      )}
      {asset.tenant_id && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <Select value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 240 }}>
            <option value="">{cur ? "— reassign to —" : "— allocate to active driver —"}</option>
            {(active.data?.drivers ?? []).map((dr) => (
              <option key={dr.driver_id} value={dr.driver_id}>{dr.name} · {dr.code} · {dr.msisdn}</option>
            ))}
          </Select>
          <SearchInput type="number" mono min={1} max={365} value={lease} style={{ width: 190 }}
            placeholder={`app lease — blank = ${info.data?.lease_days_default ?? 7} d`}
            title="How long the driver APP may keep using this asset offline before it must reach Orbit again. Blank = platform default. It never affects the vehicle — the driver rides normally regardless."
            onChange={(e) => setLease(e.target.value)} />
          <button className="btn teal" disabled={!pick || set.isPending} onClick={() => set.mutate(pick)}>
            {cur ? "Reassign" : "Allocate"}
          </button>
          {fb.errorNode}{!fb.err && msg && <span style={{ color: "var(--ok)", fontSize: 12 }}>{msg}</span>}
        </div>
      )}
      {(info.data?.history?.length ?? 0) > 1 && (<>
        <div className="section-label tight">Driver history</div>
        {info.data!.history.filter((h) => h.valid_to).map((h, i) => (
          <KV key={i} k={`${h.name} (${h.code})`} accent="var(--t-mute)"
            v={`${dateTime(h.valid_from)} → ${dateTime(h.valid_to!)}`} />
        ))}
      </>)}
    </div>
  );
}
